import { ReactNode, useCallback, useEffect, useMemo, useRef } from "react";

import ListEntitiesNew from "components/ListEntitiesNew";
import { RunWithPosition } from "types/generated";
import { FilterItem, FiltersItemsOptionsMap, SortOption } from "components/Filters/types";
import useBulkActionsSelection from "components/BulkActions/useBulkActionsSelection";
import { useBulkActionsShiftSelection } from "components/BulkActions/useBulkActionsShiftSelection";

import WorkerPoolQueuedRunsFiltersLayout from "./FiltersLayout";
import WorkerPoolQueuedRunsNoResults from "./HasNoResults";
import { getAllItemsForSelectAll } from "./helpers";
import WorkerPoolQueuedRunsVirtualizedListItem from "./ListItem/Virtualized";
import WorkerPoolQueuedRunsEmpty from "./Empty";
import WorkerPoolQueuedRunsBulkActions from "./BulkActions";

type WorkerPoolQueuedRunsViewProps = {
  entities: RunWithPosition[];
  isPageEmpty: boolean;
  hasNoFilteringResults: boolean;
  loadingIndication: boolean;
  children?: ReactNode;
  onBulkActionsFinish: () => Promise<void>;
  isPublic?: boolean;
  filters: FilterItem[];
  filtersMap: FiltersItemsOptionsMap;
  sortOptions: SortOption[];
  filtersLoading: boolean;
  refetchActiveSections: (fields: string[]) => void;
  filteredCount: number;
};

const getId = (entity: RunWithPosition) => entity.run.id;

const WorkerPoolQueuedRunsView = ({
  entities,
  isPageEmpty,
  hasNoFilteringResults,
  loadingIndication,
  children,
  onBulkActionsFinish,
  filters,
  filtersMap,
  sortOptions,
  filtersLoading,
  refetchActiveSections,
  filteredCount,
  isPublic,
}: WorkerPoolQueuedRunsViewProps) => {
  const {
    allSelected,
    selectedSet,
    syncAllSelectedItems,
    selectItem,
    unselectItem,
    onBulkSelectAll,
    onBulkResetAll,
    syncSelectedItemsWithVisibleOnes,
    onBulkContinueWith,
  } = useBulkActionsSelection();

  const memoizedRunsMap = useMemo(
    () => new Map(entities.map((edge) => [edge.run.id, { ...edge, id: edge.run.id }])),
    [entities]
  );

  const handleBulkSelectAll = useCallback(() => {
    onBulkSelectAll(getAllItemsForSelectAll(entities));
  }, [entities, onBulkSelectAll]);

  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);

  // mark selected new loaded modules if allSelected is true
  useEffect(() => {
    if (allSelected) {
      syncAllSelectedItems(getAllItemsForSelectAll(entities));
    }
  }, [allSelected, entities, syncAllSelectedItems]);

  // sync the selected items with the visible items on the list
  useEffect(() => {
    if (selectedSet.size) {
      syncSelectedItemsWithVisibleOnes(memoizedRunsMap);
    }
  }, [memoizedRunsMap, selectedSet.size, syncSelectedItemsWithVisibleOnes]);

  const onSelection = useBulkActionsShiftSelection({
    collection: entities,
    getId,
    selectedIds: selectedSet,
    selectItem,
    unselectItem,
  });

  return (
    <>
      {children}
      <WorkerPoolQueuedRunsFiltersLayout
        filters={filters}
        filtersMap={filtersMap}
        sortOptions={sortOptions}
        filtersLoading={filtersLoading}
        refetchActiveSections={refetchActiveSections}
        filteredCount={filteredCount}
        isPageEmpty={isPageEmpty}
        renderedEntitiesCount={entities.length}
        loadingIndication={loadingIndication}
        isPublic={isPublic}
        allSelected={allSelected}
        onSelectAll={handleBulkSelectAll}
        onResetAll={onBulkResetAll}
      >
        {isPageEmpty && <WorkerPoolQueuedRunsEmpty />}

        {hasNoFilteringResults && <WorkerPoolQueuedRunsNoResults />}

        {entities.length > 0 && (
          <ListEntitiesNew
            itemCount={entities.length}
            itemProps={{
              isPublic,
              items: entities,
              selectedSet,
              onCheckItem: onSelection,
            }}
            virtualizedItem={WorkerPoolQueuedRunsVirtualizedListItem}
            itemKey={(index) => entities[index].run.id}
            outerContainerRef={virtualizedListContainerRef}
          />
        )}
        <WorkerPoolQueuedRunsBulkActions
          virtualizedListContainerRef={virtualizedListContainerRef}
          selectedSet={selectedSet}
          runsMap={memoizedRunsMap}
          onBulkResetAll={onBulkResetAll}
          onFinish={onBulkActionsFinish}
          onItemDismiss={unselectItem}
          onBulkContinueWith={onBulkContinueWith}
          isPublicWorkerPool={!!isPublic}
        />
      </WorkerPoolQueuedRunsFiltersLayout>
    </>
  );
};

export default WorkerPoolQueuedRunsView;
